import "core-js/modules/es6.regexp.match.js";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import "core-js/modules/web.dom.iterable.js";
import "core-js/modules/es6.array.iterator.js";
import Vue from 'vue';
import fetch from 'unfetch';
import middleware from './middleware.js';
import { applyAsyncData, promisify, middlewareSeries, sanitizeComponent, resolveRouteComponents, getMatchedComponents, getMatchedComponentsInstances, flatMapComponents, setContext, getLocation, compile, getQueryDiff, globalHandleError, isSamePath } from './utils.js';
import { createApp, NuxtError } from './index.js';
import fetchMixin from './mixins/fetch.client';
import NuxtLink from './components/nuxt-link.client.js'; // should be included after ./index.js
// Fetch mixin

if (!Vue.__nuxt__fetch__mixin__) {
  Vue.mixin(fetchMixin);
  Vue.__nuxt__fetch__mixin__ = true;
} // Component: <NuxtLink>


Vue.component(NuxtLink.name, NuxtLink);
Vue.component('NLink', NuxtLink);

if (!global.fetch) {
  global.fetch = fetch;
} // Global shared references


var _lastPaths = [];
var app;
var router;
var store; // Try to rehydrate SSR data from window

var NUXT = window.__NUXT__ || {};
Object.assign(Vue.config, {
  "silent": true,
  "performance": false
});
var errorHandler = Vue.config.errorHandler || console.error; // Create and mount App

createApp(null, NUXT.config).then(mountApp).catch(errorHandler);

function componentOption(component, key) {
  if (!component || !component.options || !component.options[key]) {
    return {};
  }

  var option = component.options[key];

  if (typeof option === 'function') {
    for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      args[_key - 2] = arguments[_key];
    }

    return option(...args);
  }

  return option;
}

function mapTransitions(toComponents, to, from) {
  var componentTransitions = component => {
    var transition = componentOption(component, 'transition', to, from) || {};
    return typeof transition === 'string' ? {
      name: transition
    } : transition;
  };

  var fromComponents = from ? getMatchedComponents(from) : [];
  var maxDepth = Math.max(toComponents.length, fromComponents.length);
  var mergedTransitions = [];

  var _loop = function _loop(i) {
    // Clone original objects to prevent overrides
    var toTransitions = Object.assign({}, componentTransitions(toComponents[i]));
    var transitions = Object.assign({}, componentTransitions(fromComponents[i])); // Combine transitions & prefer `leave` properties of "from" route

    Object.keys(toTransitions).filter(key => typeof toTransitions[key] !== 'undefined' && !key.toLowerCase().includes('leave')).forEach(key => {
      transitions[key] = toTransitions[key];
    });
    mergedTransitions.push(transitions);
  };

  for (var i = 0; i < maxDepth; i++) {
    _loop(i);
  }

  return mergedTransitions;
}

function loadAsyncComponents(_x, _x2, _x3) {
  return _loadAsyncComponents.apply(this, arguments);
}

function _loadAsyncComponents() {
  _loadAsyncComponents = _asyncToGenerator(function* (to, from, next) {
    // Check if route changed (this._routeChanged), only if the page is not an error (for validate())
    this._routeChanged = Boolean(app.nuxt.err) || from.name !== to.name;
    this._paramChanged = !this._routeChanged && from.path !== to.path;
    this._queryChanged = !this._paramChanged && from.fullPath !== to.fullPath;
    this._diffQuery = this._queryChanged ? getQueryDiff(to.query, from.query) : [];

    if ((this._routeChanged || this._paramChanged) && this.$loading.start && !this.$loading.manual) {
      this.$loading.start();
    }

    try {
      if (this._queryChanged) {
        var Components = yield resolveRouteComponents(to, (Component, instance) => ({
          Component,
          instance
        })); // Add a marker on each component that it needs to refresh or not

        var startLoader = Components.some((_ref2) => {
          var {
            Component,
            instance
          } = _ref2;
          var watchQuery = Component.options.watchQuery;

          if (watchQuery === true) {
            return true;
          }

          if (Array.isArray(watchQuery)) {
            return watchQuery.some(key => this._diffQuery[key]);
          }

          if (typeof watchQuery === 'function') {
            return watchQuery.apply(instance, [to.query, from.query]);
          }

          return false;
        });

        if (startLoader && this.$loading.start && !this.$loading.manual) {
          this.$loading.start();
        }
      } // Call next()


      next();
    } catch (error) {
      var err = error || {};
      var statusCode = err.statusCode || err.status || err.response && err.response.status || 500;
      var message = err.message || ''; // Handle chunk loading errors
      // This may be due to a new deployment or a network problem

      if (/^Loading( CSS)? chunk (\d)+ failed\./.test(message)) {
        window.location.reload(true
        /* skip cache */
        );
        return; // prevent error page blinking for user
      }

      this.error({
        statusCode,
        message
      });
      this.$nuxt.$emit('routeChanged', to, from, err);
      next();
    }
  });
  return _loadAsyncComponents.apply(this, arguments);
}

function applySSRData(Component, ssrData) {
  if (NUXT.serverRendered && ssrData) {
    applyAsyncData(Component, ssrData);
  }

  Component._Ctor = Component;
  return Component;
} // Get matched components


function resolveComponents(router) {
  var path = getLocation(router.options.base, router.options.mode);
  return flatMapComponents(router.match(path), /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(function* (Component, _, match, key, index) {
      // If component is not resolved yet, resolve it
      if (typeof Component === 'function' && !Component.options) {
        Component = yield Component();
      } // Sanitize it and save it


      var _Component = applySSRData(sanitizeComponent(Component), NUXT.data ? NUXT.data[index] : null);

      match.components[key] = _Component;
      return _Component;
    });

    return function (_x4, _x5, _x6, _x7, _x8) {
      return _ref.apply(this, arguments);
    };
  }());
}

function callMiddleware(Components, context, layout) {
  var midd = ["nuxt-custom-headers"];
  var unknownMiddleware = false; // If layout is undefined, only call global middleware

  if (typeof layout !== 'undefined') {
    midd = []; // Exclude global middleware if layout defined (already called before)

    layout = sanitizeComponent(layout);

    if (layout.options.middleware) {
      midd = midd.concat(layout.options.middleware);
    }

    Components.forEach(Component => {
      if (Component.options.middleware) {
        midd = midd.concat(Component.options.middleware);
      }
    });
  }

  midd = midd.map(name => {
    if (typeof name === 'function') {
      return name;
    }

    if (typeof middleware[name] !== 'function') {
      unknownMiddleware = true;
      this.error({
        statusCode: 500,
        message: 'Unknown middleware ' + name
      });
    }

    return middleware[name];
  });

  if (unknownMiddleware) {
    return;
  }

  return middlewareSeries(midd, context);
}

function render(_x9, _x10, _x11) {
  return _render.apply(this, arguments);
} // Fix components format in matched, it's due to code-splitting of vue-router


function _render() {
  _render = _asyncToGenerator(function* (to, from, next) {
    var _this = this;

    if (this._routeChanged === false && this._paramChanged === false && this._queryChanged === false) {
      return next();
    } // Handle first render on SPA mode


    var spaFallback = false;

    if (to === from) {
      _lastPaths = [];
      spaFallback = true;
    } else {
      var fromMatches = [];
      _lastPaths = getMatchedComponents(from, fromMatches).map((Component, i) => {
        return compile(from.matched[fromMatches[i]].path)(from.params);
      });
    } // nextCalled is true when redirected


    var nextCalled = false;

    var _next = path => {
      if (from.path === path.path && this.$loading.finish) {
        this.$loading.finish();
      }

      if (from.path !== path.path && this.$loading.pause) {
        this.$loading.pause();
      }

      if (nextCalled) {
        return;
      }

      nextCalled = true;
      next(path);
    }; // Update context


    yield setContext(app, {
      route: to,
      from,
      next: _next.bind(this)
    });
    this._dateLastError = app.nuxt.dateErr;
    this._hadError = Boolean(app.nuxt.err); // Get route's matched components

    var matches = [];
    var Components = getMatchedComponents(to, matches); // If no Components matched, generate 404

    if (!Components.length) {
      // Default layout
      yield callMiddleware.call(this, Components, app.context);

      if (nextCalled) {
        return;
      } // Load layout for error page


      var errorLayout = (NuxtError.options || NuxtError).layout;
      var layout = yield this.loadLayout(typeof errorLayout === 'function' ? errorLayout.call(NuxtError, app.context) : errorLayout);
      yield callMiddleware.call(this, Components, app.context, layout);

      if (nextCalled) {
        return;
      } // Show error page


      app.context.error({
        statusCode: 404,
        message: 'This page could not be found'
      });
      return next();
    } // Update ._data and other properties if hot reloaded


    Components.forEach(Component => {
      if (Component._Ctor && Component._Ctor.options) {
        Component.options.asyncData = Component._Ctor.options.asyncData;
        Component.options.fetch = Component._Ctor.options.fetch;
      }
    }); // Apply transitions

    this.setTransitions(mapTransitions(Components, to, from));

    try {
      // Call middleware
      yield callMiddleware.call(this, Components, app.context);

      if (nextCalled) {
        return;
      }

      if (app.context._errored) {
        return next();
      } // Set layout


      var _layout = Components[0].options.layout;

      if (typeof _layout === 'function') {
        _layout = _layout(app.context);
      }

      _layout = yield this.loadLayout(_layout); // Call middleware for layout

      yield callMiddleware.call(this, Components, app.context, _layout);

      if (nextCalled) {
        return;
      }

      if (app.context._errored) {
        return next();
      } // Call .validate()


      var isValid = true;

      try {
        for (var Component of Components) {
          if (typeof Component.options.validate !== 'function') {
            continue;
          }

          isValid = yield Component.options.validate(app.context);

          if (!isValid) {
            break;
          }
        }
      } catch (validationError) {
        // ...If .validate() threw an error
        this.error({
          statusCode: validationError.statusCode || '500',
          message: validationError.message
        });
        return next();
      } // ...If .validate() returned false


      if (!isValid) {
        this.error({
          statusCode: 404,
          message: 'This page could not be found'
        });
        return next();
      }

      var instances; // Call asyncData & fetch hooks on components matched by the route.

      yield Promise.all(Components.map( /*#__PURE__*/function () {
        var _ref3 = _asyncToGenerator(function* (Component, i) {
          // Check if only children route changed
          Component._path = compile(to.matched[matches[i]].path)(to.params);
          Component._dataRefresh = false;
          var childPathChanged = Component._path !== _lastPaths[i]; // Refresh component (call asyncData & fetch) when:
          // Route path changed part includes current component
          // Or route param changed part includes current component and watchParam is not `false`
          // Or route query is changed and watchQuery returns `true`

          if (_this._routeChanged && childPathChanged) {
            Component._dataRefresh = true;
          } else if (_this._paramChanged && childPathChanged) {
            var watchParam = Component.options.watchParam;
            Component._dataRefresh = watchParam !== false;
          } else if (_this._queryChanged) {
            var watchQuery = Component.options.watchQuery;

            if (watchQuery === true) {
              Component._dataRefresh = true;
            } else if (Array.isArray(watchQuery)) {
              Component._dataRefresh = watchQuery.some(key => _this._diffQuery[key]);
            } else if (typeof watchQuery === 'function') {
              if (!instances) {
                instances = getMatchedComponentsInstances(to);
              }

              Component._dataRefresh = watchQuery.apply(instances[i], [to.query, from.query]);
            }
          }

          if (!_this._hadError && _this._isMounted && !Component._dataRefresh) {
            return;
          }

          var promises = [];
          var hasAsyncData = Component.options.asyncData && typeof Component.options.asyncData === 'function';
          var hasFetch = Boolean(Component.options.fetch) && Component.options.fetch.length;
          var loadingIncrease = hasAsyncData && hasFetch ? 30 : 45; // Call asyncData(context)

          if (hasAsyncData) {
            var promise = promisify(Component.options.asyncData, app.context);
            promise.then(asyncDataResult => {
              applyAsyncData(Component, asyncDataResult);

              if (_this.$loading.increase) {
                _this.$loading.increase(loadingIncrease);
              }
            });
            promises.push(promise);
          } // Check disabled page loading


          _this.$loading.manual = Component.options.loading === false; // Call fetch(context)

          if (hasFetch) {
            var p = Component.options.fetch(app.context);

            if (!p || !(p instanceof Promise) && typeof p.then !== 'function') {
              p = Promise.resolve(p);
            }

            p.then(fetchResult => {
              if (_this.$loading.increase) {
                _this.$loading.increase(loadingIncrease);
              }
            });
            promises.push(p);
          }

          return Promise.all(promises);
        });

        return function (_x14, _x15) {
          return _ref3.apply(this, arguments);
        };
      }())); // If not redirected

      if (!nextCalled) {
        if (this.$loading.finish && !this.$loading.manual) {
          this.$loading.finish();
        }

        next();
      }
    } catch (err) {
      var error = err || {};

      if (error.message === 'ERR_REDIRECT') {
        return this.$nuxt.$emit('routeChanged', to, from, error);
      }

      _lastPaths = [];
      globalHandleError(error); // Load error layout

      var _layout2 = (NuxtError.options || NuxtError).layout;

      if (typeof _layout2 === 'function') {
        _layout2 = _layout2(app.context);
      }

      yield this.loadLayout(_layout2);
      this.error(error);
      this.$nuxt.$emit('routeChanged', to, from, error);
      next();
    }
  });
  return _render.apply(this, arguments);
}

function normalizeComponents(to, ___) {
  flatMapComponents(to, (Component, _, match, key) => {
    if (typeof Component === 'object' && !Component.options) {
      // Updated via vue-router resolveAsyncComponents()
      Component = Vue.extend(Component);
      Component._Ctor = Component;
      match.components[key] = Component;
    }

    return Component;
  });
}

function setLayoutForNextPage(_x12) {
  return _setLayoutForNextPage.apply(this, arguments);
}

function _setLayoutForNextPage() {
  _setLayoutForNextPage = _asyncToGenerator(function* (to) {
    // Set layout
    var hasError = Boolean(this.$options.nuxt.err);

    if (this._hadError && this._dateLastError === this.$options.nuxt.dateErr) {
      hasError = false;
    }

    var layout = hasError ? (NuxtError.options || NuxtError).layout : to.matched[0].components.default.options.layout;

    if (typeof layout === 'function') {
      layout = layout(app.context);
    }

    yield this.loadLayout(layout);
    this.setLayout(layout);
  });
  return _setLayoutForNextPage.apply(this, arguments);
}

function checkForErrors(app) {
  // Hide error component if no error
  if (app._hadError && app._dateLastError === app.$options.nuxt.dateErr) {
    app.error();
  }
} // When navigating on a different route but the same component is used, Vue.js
// Will not update the instance data, so we have to update $data ourselves


function fixPrepatch(to, ___) {
  if (this._routeChanged === false && this._paramChanged === false && this._queryChanged === false) {
    return;
  }

  var instances = getMatchedComponentsInstances(to);
  var Components = getMatchedComponents(to);
  var triggerScroll = false;
  Vue.nextTick(() => {
    instances.forEach((instance, i) => {
      if (!instance || instance._isDestroyed) {
        return;
      }

      if (instance.constructor._dataRefresh && Components[i] === instance.constructor && instance.$vnode.data.keepAlive !== true && typeof instance.constructor.options.data === 'function') {
        var newData = instance.constructor.options.data.call(instance);

        for (var key in newData) {
          Vue.set(instance.$data, key, newData[key]);
        }

        triggerScroll = true;
      }
    });

    if (triggerScroll) {
      // Ensure to trigger scroll event after calling scrollBehavior
      window.$nuxt.$nextTick(() => {
        window.$nuxt.$emit('triggerScroll');
      });
    }

    checkForErrors(this);
  });
}

function nuxtReady(_app) {
  window.onNuxtReadyCbs.forEach(cb => {
    if (typeof cb === 'function') {
      cb(_app);
    }
  }); // Special JSDOM

  if (typeof window._onNuxtLoaded === 'function') {
    window._onNuxtLoaded(_app);
  } // Add router hooks


  router.afterEach((to, from) => {
    // Wait for fixPrepatch + $data updates
    Vue.nextTick(() => _app.$nuxt.$emit('routeChanged', to, from));
  });
}

function mountApp(_x13) {
  return _mountApp.apply(this, arguments);
}

function _mountApp() {
  _mountApp = _asyncToGenerator(function* (__app) {
    // Set global variables
    app = __app.app;
    router = __app.router;
    store = __app.store; // Create Vue instance

    var _app = new Vue(app); // Load layout


    var layout = NUXT.layout || 'default';
    yield _app.loadLayout(layout);

    _app.setLayout(layout); // Mounts Vue app to DOM element


    var mount = () => {
      _app.$mount('#__nuxt'); // Add afterEach router hooks


      router.afterEach(normalizeComponents);
      router.afterEach(setLayoutForNextPage.bind(_app));
      router.afterEach(fixPrepatch.bind(_app)); // Listen for first Vue update

      Vue.nextTick(() => {
        // Call window.{{globals.readyCallback}} callbacks
        nuxtReady(_app);
      });
    }; // Resolve route components


    var Components = yield Promise.all(resolveComponents(router)); // Enable transitions

    _app.setTransitions = _app.$options.nuxt.setTransitions.bind(_app);

    if (Components.length) {
      _app.setTransitions(mapTransitions(Components, router.currentRoute));

      _lastPaths = router.currentRoute.matched.map(route => compile(route.path)(router.currentRoute.params));
    } // Initialize error handler


    _app.$loading = {}; // To avoid error while _app.$nuxt does not exist

    if (NUXT.error) {
      _app.error(NUXT.error);
    } // Add beforeEach router hooks


    router.beforeEach(loadAsyncComponents.bind(_app));
    router.beforeEach(render.bind(_app)); // Fix in static: remove trailing slash to force hydration
    // Full static, if server-rendered: hydrate, to allow custom redirect to generated page
    // Fix in static: remove trailing slash to force hydration

    if (NUXT.serverRendered && isSamePath(NUXT.routePath, _app.context.route.path)) {
      return mount();
    } // First render on client-side


    var clientFirstMount = () => {
      normalizeComponents(router.currentRoute, router.currentRoute);
      setLayoutForNextPage.call(_app, router.currentRoute);
      checkForErrors(_app); // Don't call fixPrepatch.call(_app, router.currentRoute, router.currentRoute) since it's first render

      mount();
    }; // fix: force next tick to avoid having same timestamp when an error happen on spa fallback


    yield new Promise(resolve => setTimeout(resolve, 0));
    render.call(_app, router.currentRoute, router.currentRoute, path => {
      // If not redirected
      if (!path) {
        clientFirstMount();
        return;
      } // Add a one-time afterEach hook to
      // mount the app wait for redirect and route gets resolved


      var unregisterHook = router.afterEach((to, from) => {
        unregisterHook();
        clientFirstMount();
      }); // Push the path and let route to be resolved

      router.push(path, undefined, err => {
        if (err) {
          errorHandler(err);
        }
      });
    });
  });
  return _mountApp.apply(this, arguments);
}